import React from 'react'
import { FileText, AlertTriangle, Clock, CheckCircle, RefreshCw } from 'lucide-react'
import { WeekLockStatus } from '../../services/timesheetService'

interface WeekLockStatusBannerProps {
  lockStatus: WeekLockStatus
  onRefresh: () => void
  isRefreshing?: boolean
}

const WeekLockStatusBanner: React.FC<WeekLockStatusBannerProps> = ({ 
  lockStatus, 
  onRefresh,
  isRefreshing = false
}) => {
  const getStatusConfig = (status: string | null) => {
    switch (status) {
      case 'DRAFT':
        return {
          type: 'info',
          icon: <FileText className="w-5 h-5" />,
          bgColor: 'bg-blue-50',
          borderColor: 'border-blue-200',
          textColor: 'text-blue-800',
          title: '草稿狀態',
          message: '您可以繼續修改時間記錄'
        }
      
      case 'REJECTED':
        return {
          type: 'warning',
          icon: <AlertTriangle className="w-5 h-5" />,
          bgColor: 'bg-yellow-50',
          borderColor: 'border-yellow-200',
          textColor: 'text-yellow-800',
          title: '申報已駁回',
          message: '您可以修改時間記錄後重新提交'
        }
      
      case 'SUBMITTED':
        return {
          type: 'warning',
          icon: <Clock className="w-5 h-5" />,
          bgColor: 'bg-orange-50',
          borderColor: 'border-orange-200',
          textColor: 'text-orange-800',
          title: '審核中',
          message: '申報已提交，如需修改請在申報頁面撤回申報'
        }
      
      case 'APPROVED':
        return {
          type: 'success',
          icon: <CheckCircle className="w-5 h-5" />,
          bgColor: 'bg-green-50',
          borderColor: 'border-green-200',
          textColor: 'text-green-800',
          title: '已核准',
          message: '申報已核准，無法再修改時間記錄'
        }
      
      default:
        return null
    }
  }

  const config = getStatusConfig(lockStatus.status)
  if (!config) return null

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    })
  }

  return (
    <div className={`${config.bgColor} ${config.borderColor} border rounded-lg p-4 mb-6`}>
      <div className="flex items-start">
        <div className={`${config.textColor} mr-3 mt-0.5 flex-shrink-0`}>
          {config.icon}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className={`font-medium ${config.textColor} mb-2`}>
            {config.title}
          </h3>
          <p className={`${config.textColor} mb-3 text-sm`}>
            {config.message}
          </p>
          
          {/* 週次範圍 */}
          <div className="mb-3">
            <p className={`${config.textColor} text-sm`}>
              <span className="font-medium">週次範圍：</span>
              {formatDate(lockStatus.weekStart)} - {formatDate(lockStatus.weekEnd)}
            </p>
          </div>
          
          {/* 駁回原因 */}
          {lockStatus.status === 'REJECTED' && lockStatus.rejectionReason && (
            <div className="mb-3">
              <p className={`${config.textColor} font-medium mb-1 text-sm`}>
                駁回原因：
              </p>
              <div className={`${config.bgColor} border ${config.borderColor} rounded p-2`}>
                <p className={`${config.textColor} text-sm leading-relaxed`}>
                  {lockStatus.rejectionReason}
                </p>
              </div>
            </div>
          )}
          
          {/* 修改期限 */}
          {lockStatus.status === 'REJECTED' && lockStatus.revisionDeadline && (
            <div className="mb-3">
              <p className={`${config.textColor} font-medium mb-1 text-sm`}>
                修改期限：
              </p>
              <p className={`${config.textColor} text-sm`}>
                {formatDate(lockStatus.revisionDeadline)}
              </p>
            </div>
          )}
          
          {/* 操作建議和重新檢查按鈕 */}
          <div className="flex items-center justify-between">
            <p className={`${config.textColor} text-xs`}>
              {lockStatus.isLocked ? '如需修改，請聯繫管理員' : ''}
            </p>
            <button
              onClick={onRefresh}
              disabled={isRefreshing}
              className={`
                flex items-center gap-1 text-xs ${config.textColor} 
                hover:underline focus:outline-none focus:underline
                disabled:opacity-50 disabled:cursor-not-allowed
              `}
            >
              <RefreshCw className={`w-3 h-3 ${isRefreshing ? 'animate-spin' : ''}`} />
              {isRefreshing ? '檢查中...' : '重新檢查狀態'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default WeekLockStatusBanner
